'use client';

import * as React from 'react';
import {
  ChevronLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  PlayCircle,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useListOperations } from '@/lib/api/subscriptionManager';
import { cn } from "@/lib/utils";

interface OperationHistoryProps {
  onBack: () => void;
  onSelectOperation: (operationId: string) => void;
}

const ACTION_LABELS: Record<string, string> = {
  processor_change: 'Change Processor',
  billing_update: 'Update Billing',
  distribute_rebills: 'Distribute Rebills',
  pause: 'Pause',
  resume: 'Resume',
  cancel: 'Cancel',
  retry_new_processor: 'Retry Processor',
  set_tagada_vault: 'Tagada Vault',
};

// `retry_new_processor` covers two different operations: moving the next attempt, and charging
// right away. Only the action config tells them apart, so the type alone cannot label the row.
const labelFor = (op: { type: string; actionConfig?: { retryMode?: string } }) =>
  op.actionConfig?.retryMode === 'charge_now' ? 'Retry Payment' : ACTION_LABELS[op.type] || op.type;

const STATUS_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  running: { icon: PlayCircle, color: 'text-blue-600', bg: 'bg-blue-100' },
  completed: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100' },
  partially_completed: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-100' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' },
  rolled_back: { icon: RotateCcw, color: 'text-gray-600', bg: 'bg-gray-100' },
};

function formatDate(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date));
}

export function OperationHistory({ onBack, onSelectOperation }: OperationHistoryProps) {
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const pageSize = 10;

  const { data, isLoading } = useListOperations(
    { page, pageSize },
    statusFilter.length > 0 ? statusFilter : undefined
  );

  const toggleStatus = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
    setPage(1);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <h2 className="text-lg font-semibold">Operation History</h2>
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const isSelected = statusFilter.includes(status);
          const Icon = config.icon;
          return (
            <button
              key={status}
              onClick={() => toggleStatus(status)}
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                isSelected
                  ? `${config.bg} ${config.color}`
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              <Icon className="h-3 w-3" />
              {status.replace('_', ' ')}
            </button>
          );
        })}
      </div>

      {/* Operations List */}
      <ScrollArea className="flex-1 -mx-1 px-1">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : !data?.operations.length ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No operations found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.operations.map((op) => {
              const statusConfig = STATUS_CONFIG[op.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;

              return (
                <button
                  key={op.id}
                  onClick={() => onSelectOperation(op.id)}
                  className="w-full text-left p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className={cn('p-1.5 rounded-full', statusConfig.bg)}>
                        <StatusIcon className={cn('h-4 w-4', statusConfig.color)} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {labelFor(op)}
                          </span>
                          {op.rollbackOfOperationId && (
                            <Badge variant="outline" className="text-xs">
                              Rollback
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {formatDate(op.createdAt)}
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-sm font-medium">
                        {op.successCount}/{op.targetCount}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {op.failedCount > 0 && (
                          <span className="text-red-600">{op.failedCount} failed</span>
                        )}
                        {op.failedCount === 0 && op.status === 'completed' && (
                          <span className="text-green-600">All success</span>
                        )}
                        {op.status === 'running' && (
                          <span className="text-blue-600">In progress</span>
                        )}
                        {op.status === 'pending' && (
                          <span className="text-yellow-600">Queued</span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={cn(page === 1 && 'pointer-events-none opacity-50')}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => setPage(pageNum)}
                      isActive={page === pageNum}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  className={cn(page === data.totalPages && 'pointer-events-none opacity-50')}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
